/**
 * AdvertiserRbacPage — role management for the advertiser team.
 *
 * Shows existing roles, lets the user create a new role (name + perm
 * selection from the closed catalogue), and delete roles. Brand: emerald
 * headline, sky accents, zinc borders, 6-8px rounding. No forbidden colors.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAdvertiserRbacCreate,
	useAdvertiserRbacDelete,
	useAdvertiserRbacPerms,
	useAdvertiserRbacRoles,
} from "./hooks";
import type { PermSlug } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const CHIP =
	"inline-flex items-center rounded-[6px] border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700";

function formatTimestamp(unix: number): string {
	if (!Number.isFinite(unix)) return "-";
	try {
		return new Date(unix * 1000).toISOString().slice(0, 19).replace("T", " ");
	} catch {
		return "-";
	}
}

export function AdvertiserRbacPage() {
	const rolesQuery = useAdvertiserRbacRoles();
	const permsQuery = useAdvertiserRbacPerms();
	const createMut = useAdvertiserRbacCreate();
	const deleteMut = useAdvertiserRbacDelete();

	const [name, setName] = useState("");
	const [selected, setSelected] = useState<Set<PermSlug>>(new Set());

	const perms = useMemo(
		() => permsQuery.data?.perms ?? [],
		[permsQuery.data],
	);
	const roles = rolesQuery.data?.roles ?? [];

	const togglePerm = (p: PermSlug) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(p)) next.delete(p);
			else next.add(p);
			return next;
		});
	};

	const resetForm = () => {
		setName("");
		setSelected(new Set());
	};

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = name.trim();
		if (!trimmed) return;
		createMut.mutate(
			{ name: trimmed, permissions: Array.from(selected) },
			{ onSuccess: () => resetForm() },
		);
	};

	return (
		<main
			data-testid="advertiser-rbac-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					advertiser-rbac
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Advertiser RBAC
				</h1>
				<p className="text-sm text-zinc-600">
					Scoped roles for the advertiser team. Select perms from the closed
					catalogue; assign roles to sub-users via the BE.
				</p>
			</header>

			<section className={CARD} aria-label="Create role">
				<h2 className="text-base font-semibold text-zinc-900">Create role</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 flex flex-col gap-4"
					data-testid="advertiser-rbac-create-form"
				>
					<label className="flex flex-col gap-1 text-sm text-zinc-700">
						<span className="font-medium">Role name</span>
						<input
							type="text"
							className={INPUT}
							placeholder="e.g. campaign-editor"
							value={name}
							onChange={(e) => setName(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="advertiser-rbac-name-input"
						/>
					</label>
					<fieldset className="flex flex-col gap-2 text-sm text-zinc-700">
						<legend className="font-medium">Permissions</legend>
						{permsQuery.isLoading ? (
							<p
								className="text-xs text-zinc-500"
								data-testid="advertiser-rbac-perms-loading"
							>
								Loading perms.
							</p>
						) : permsQuery.error ? (
							<p className="text-xs text-red-600" role="alert">
								{permsQuery.error.message}
							</p>
						) : perms.length === 0 ? (
							<p
								className="text-xs text-zinc-500"
								data-testid="advertiser-rbac-perms-empty"
							>
								No perms available.
							</p>
						) : (
							<div
								className="flex flex-wrap gap-2"
								data-testid="advertiser-rbac-perms-list"
							>
								{perms.map((p) => {
									const active = selected.has(p);
									return (
										<label
											key={p}
											className={
												"flex cursor-pointer items-center gap-1.5 rounded-[6px] border px-2.5 py-1 text-xs " +
												(active
													? "border-green-600 bg-sky-50 text-sky-700"
													: "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50")
											}
										>
											<input
												type="checkbox"
												className="h-3.5 w-3.5 accent-green-600"
												checked={active}
												onChange={() => togglePerm(p)}
												disabled={createMut.isPending}
												data-testid={`advertiser-rbac-perm-${p}`}
											/>
											<span className="font-mono">{p}</span>
										</label>
									);
								})}
							</div>
						)}
					</fieldset>
					<div>
						<button
							type="submit"
							className={BTN}
							disabled={createMut.isPending || !name.trim()}
							data-testid="advertiser-rbac-submit"
						>
							{createMut.isPending ? "Saving." : "Create role"}
						</button>
					</div>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Roles">
				<h2 className="text-base font-semibold text-zinc-900">Roles</h2>
				{rolesQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-rbac-loading"
					>
						Loading.
					</p>
				) : rolesQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{rolesQuery.error.message}
					</p>
				) : roles.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-rbac-empty"
					>
						No roles yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="advertiser-rbac-list"
					>
						{roles.map((role) => (
							<li
								key={role.id}
								className="flex flex-col gap-2 py-3"
								data-testid={`advertiser-rbac-row-${role.id}`}
							>
								<div className="flex items-center justify-between gap-3">
									<div className="flex flex-col gap-0.5">
										<span className="text-sm font-medium text-zinc-900">
											{role.name}
										</span>
										<span className="font-mono text-xs text-zinc-500">
											{role.id}
										</span>
									</div>
									<button
										type="button"
										className={BTN_GHOST}
										onClick={() => deleteMut.mutate(role.id)}
										disabled={deleteMut.isPending}
										data-testid={`advertiser-rbac-delete-${role.id}`}
									>
										Delete
									</button>
								</div>
								<div className="flex flex-wrap gap-1.5">
									{role.permissions.length === 0 ? (
										<span className="text-xs text-zinc-500">
											No perms granted.
										</span>
									) : (
										role.permissions.map((p) => (
											<span key={p} className={CHIP}>
												{p}
											</span>
										))
									)}
								</div>
								<p className="text-xs text-zinc-500">
									Updated {formatTimestamp(role.updated_at)}
								</p>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AdvertiserRbacPage;
